import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import "../Views/Utilisateure/visiteur/EspaceVisiteur/SupportReclamation.css";

interface CreateReclamationPopupProps {
  isOpen: boolean;
  onClose: () => void;
  refId: string; // Ex: "CMD-12" or "DEV-34"
  type: "commande" | "devis";
  onSuccess?: (refId: string) => void;
}

const PROBLEM_TYPES = [
  { value: "commande_non_recue", label: "Commande non reçue" },
  { value: "devis_refuse", label: "Devis non accepté / refusé" },
  { value: "paiement", label: "Problème de paiement" },
  { value: "article_endommage", label: "Article endommagé / incorrect" },
  { value: "autre", label: "Autre demande" }
];

export default function CreateReclamationPopup({
  isOpen,
  onClose,
  refId,
  type,
  onSuccess
}: CreateReclamationPopupProps) {
  const visiteurId = localStorage.getItem("VisiteurId") || "";
  const visiteurName = localStorage.getItem("VisiteurName") || "";
  const visiteurEmail = localStorage.getItem("VisiteurEmail") || "";
  const visiteurTele = localStorage.getItem("VisiteurTele") || "";

  // Prefill fields
  const [nom, setNom] = useState(visiteurName);
  const [email, setEmail] = useState(visiteurEmail);
  const [telephone, setTelephone] = useState(visiteurTele);
  const [typeProbleme, setTypeProbleme] = useState(
    type === "devis" ? "devis_refuse" : "commande_non_recue"
  );
  const [description, setDescription] = useState("");
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [successCode, setSuccessCode] = useState<string | null>(null);
  const [error, setError] = useState("");

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync state with localstorage when popup opens
  useEffect(() => {
    if (isOpen) {
      setNom(localStorage.getItem("VisiteurName") || "");
      setEmail(localStorage.getItem("VisiteurEmail") || "");
      setTelephone(localStorage.getItem("VisiteurTele") || "");
      setTypeProbleme(type === "devis" ? "devis_refuse" : "commande_non_recue");
      setDescription("");
      setImage(null);
      setImagePreview(null);
      setSuccessCode(null);
      setError("");
    }
  }, [isOpen, type]);

  if (!isOpen) return null;

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImage(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nom.trim()) {
      setError("Le nom complet est obligatoire.");
      return;
    }
    if (!description.trim()) {
      setError("Veuillez décrire le problème.");
      return;
    }

    setSubmitting(true);
    setError("");

    const formData = new FormData();
    formData.append("Nom", nom);
    if (email) formData.append("Email", email);
    if (telephone) formData.append("Telephone", telephone);
    formData.append("TypeProbleme", typeProbleme);
    formData.append("RefCommande", refId);
    formData.append("Description", description);
    if (visiteurId) formData.append("VisiteurId", visiteurId);
    if (image) formData.append("image", image);

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_PHP_APP_URL}/reclamations`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      setSuccessCode(res.data.CodeReclamation);
      if (onSuccess) onSuccess(refId);
    } catch (err: any) {
      setError(
        err.response?.data?.message || "Une erreur s'est produite lors de l'envoi."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0, 0, 0, 0.6)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: 20
      }}
      onClick={onClose}
    >
      <div
        className="sr-card"
        style={{
          width: "100%",
          maxWidth: 580,
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0 20px 25px -5px rgba(0,0,0,0.3)"
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sr-card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <h2>
              <i className="bi bi-shield-exclamation"></i> Réclamation {refId}
            </h2>
            <p>Déposez une réclamation pour cette référence.</p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "rgba(255, 255, 255, 0.6)",
              fontSize: "1.4rem",
              cursor: "pointer",
              padding: 0,
              lineHeight: 1
            }}
          >
            <i className="bi bi-x-lg" />
          </button>
        </div>

        <div style={{ padding: 24 }}>
          {successCode ? (
            <div style={{ textAlign: "center", padding: "16px 0" }}>
              <div
                className="sr-success-icon"
                style={{ width: 64, height: 64, fontSize: 28, marginBottom: 16 }}
              >
                <i className="bi bi-check-lg"></i>
              </div>
              <h3 style={{ fontSize: "1.3rem", fontWeight: 800, marginBottom: 8 }}>
                Réclamation enregistrée !
              </h3>
              <p style={{ color: "var(--text-sec-sr)", fontSize: "0.9rem", marginBottom: 20 }}>
                Votre réclamation a bien été créée sous le code suivant :
              </p>
              <div
                className="sr-code-box"
                style={{ padding: "12px 24px", marginBottom: 24, display: "inline-flex" }}
              >
                <span className="sr-code-value" style={{ fontSize: "1.4rem" }}>
                  {successCode}
                </span>
              </div>
              <div>
                <button
                  onClick={onClose}
                  className="sr-submit-btn"
                  style={{ display: "inline-flex", width: "auto", padding: "10px 24px" }}
                >
                  Fermer
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {error && (
                <div className="sr-alert error" style={{ marginBottom: 16 }}>
                  <i className="bi bi-exclamation-triangle"></i>
                  <span>{error}</span>
                </div>
              )}

              <div className="sr-form-row">
                <div className="sr-form-group">
                  <label className="sr-label">Nom complet <span>*</span></label>
                  <input
                    type="text"
                    required
                    className="sr-input"
                    value={nom}
                    onChange={(e) => setNom(e.target.value)}
                  />
                </div>
                <div className="sr-form-group">
                  <label className="sr-label">Email</label>
                  <input
                    type="email"
                    className="sr-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>

              <div className="sr-form-row">
                <div className="sr-form-group">
                  <label className="sr-label">Téléphone</label>
                  <input
                    type="tel"
                    className="sr-input"
                    value={telephone}
                    onChange={(e) => setTelephone(e.target.value)}
                  />
                </div>
                <div className="sr-form-group">
                  <label className="sr-label">Type de problème</label>
                  <select
                    className="sr-select"
                    value={typeProbleme}
                    onChange={(e) => setTypeProbleme(e.target.value)}
                  >
                    {PROBLEM_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="sr-form-group" style={{ marginBottom: 16 }}>
                <label className="sr-label">Description du problème <span>*</span></label>
                <textarea
                  required
                  className="sr-textarea"
                  style={{ minHeight: 90 }}
                  placeholder="Expliquez en détail le problème rencontré..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>

              <div className="sr-form-group" style={{ marginBottom: 20 }}>
                <label className="sr-label">Pièce jointe (facultatif)</label>
                <div className="sr-upload-zone" style={{ padding: "16px 20px" }}>
                  <i className="bi bi-cloud-arrow-up sr-upload-icon" style={{ fontSize: 22 }}></i>
                  <p className="sr-upload-text" style={{ fontSize: 12 }}>
                    Parcourir ou déposer une capture
                  </p>
                  <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    onChange={handleImageChange}
                  />
                </div>

                {imagePreview && (
                  <div style={{ textAlign: "center", marginTop: 8 }}>
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="sr-preview-img"
                      style={{ maxHeight: 110, width: "auto" }}
                    />
                    <div>
                      <button
                        type="button"
                        className="sr-remove-img-btn"
                        style={{ padding: "3px 10px", fontSize: 11 }}
                        onClick={removeImage}
                      >
                        <i className="bi bi-trash"></i> Retirer
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <button
                type="submit"
                className="sr-submit-btn"
                disabled={submitting}
                style={{ padding: "12px" }}
              >
                {submitting ? (
                  <>
                    <div className="sr-spinner" style={{ width: 14, height: 14 }}></div> Envoi...
                  </>
                ) : (
                  <>
                    <i className="bi bi-send-fill"></i> Envoyer la réclamation
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
